/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	AfterContentChecked,
	ChangeDetectionStrategy,
	Component,
	OnChanges,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { startWith, distinctUntilChanged, takeUntil, map } from 'rxjs/operators';
import { ColorPickerComponent } from '../../color-picker/color-picker.component';
import { AbstractAddEditItemComponent } from './abstract-add-edit-item.component';

/**
 * An abstract superclass for "add-edit" components which include a color-picker;
 * that is, editing of an item which has an associated color, namely Roles and Locations.
 */
@Component({
	selector: 'add-edit-color-item-base',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class AbstractAddEditColorItemComponent<T>
	extends AbstractAddEditItemComponent<T>
	implements OnInit, OnDestroy, OnChanges, AfterContentChecked
{
	@ViewChild('colorPickerElement', { static: false }) public colorPicker: ColorPickerComponent;

	protected colorSelected$ = new BehaviorSubject<boolean>(false);
	protected colorPickerExists$ = new BehaviorSubject<boolean>(false);
	protected pickerNeedsToBeDisplayed$ = new BehaviorSubject<boolean>(false);

	protected abstract getColorFormControlName(): string;
	protected abstract getColorFromModel(model: T): string;
	protected abstract valueForFormGroupPatchValue(
		model: T,
		color: string
	): {
		[key: string]: any;
	};

	constructor(protected dialog: MatDialog, protected builder: UntypedFormBuilder) {
		super(dialog);
	}

	public ngOnInit() {
		super.ngOnInit();
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
		this.colorSelected$.complete();
		this.colorPickerExists$.complete();
		this.pickerNeedsToBeDisplayed$.complete();
	}

	public ngOnChanges(changes) {
		super.ngOnChanges(changes);

		// need to ask color picker to refresh. needed here for other changes as well, such as palette
		if (this.colorPicker) {
			this.colorPicker.start();
		}
	}

	public ngAfterContentChecked() {
		this.colorPickerExists$.next(!!this.colorPicker);
	}

	protected createFormGroup(obj: T) {
		const result = this.builder.group({
			name: ['', Validators.required],
			[this.getColorFormControlName()]: [null, Validators.required], // set to null to force a selection
		});

		result
			.get(this.getColorFormControlName())
			.valueChanges.pipe(
				startWith(null as string),
				map((color) => !!color),
				distinctUntilChanged()
			)
			.subscribe(this.colorSelected$);

		combineLatest([
			result
				.get(this.getColorFormControlName())
				.valueChanges.pipe(startWith(null as string), distinctUntilChanged()),
			this.colorPickerExists$.pipe(distinctUntilChanged()),
			this.pickerNeedsToBeDisplayed$.pipe(distinctUntilChanged()),
		])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([color, pickerExists, pickerNeedsToBeDisplayed]) => {
				if (!color && pickerExists && pickerNeedsToBeDisplayed) {
					this.pickerNeedsToBeDisplayed$.next(false);
					// Yes it sucks that there's this 'setTimeout' call, but a collective "we" don't know of a way to get
					// it to work without it.  As Kurtis wrote:
					//
					// Doesn't seem to be a change detection timing thing.  Found out though that spectrum is a jquery plugin
					// and jquery is notorious for timeout hacks, so my best guess is that you need the timeout hack...
					// because jquery. ¯\_(ツ)_/¯
					setTimeout(() => {
						this.colorPicker.start();
						this.colorPicker.showPicker();
					});
				}
			});
		return result;
	}

	protected updateFormGroup(model: T) {
		const color = this.getColorFromModel(model);
		const value = this.valueForFormGroupPatchValue(model, color);
		this.itemFormGroup.patchValue(value, { emitEvent: false });
		this.updateSelectedColor(color);
	}

	protected updateSelectedColor(color: string): void {
		this.colorSelected$.next(!!color);

		// this same color picker is now shared among all items on the list. It is not reflecting color after the creation
		// even though it recognized the change from the color formControl. need to ask it to update.
		if (this.colorPicker) {
			this.colorPicker.color.setValue(color);
		}
	}

	public displayColorPicker() {
		this.colorSelected$.next(true);
		this.pickerNeedsToBeDisplayed$.next(true);
	}

	public handleColorPickerHide() {
		this.colorSelected$.next(!!this.itemFormGroup.get(this.getColorFormControlName()).value);
	}
}
